/**
 * ASCII Art Generator - Claude 渲染引擎
 *
 * Claude Code 风格：高密度、绿色强调、现代感
 * 支持动画渲染
 * Wave 8.3: 支持灰度预计算缓存
 */

import type { IRenderEngine, FullRenderSettings, RenderResult, StyleType, RGB } from '../types'
import type { AnimationContext } from '../animation/types'
import type { RenderContextExt } from './animationHelpers'
import { samplePixel, samplePixelFast, applyContrastAndBrightness, applyColorMode, invertBrightness } from '../algorithms/grayscale'
import { getCharByBrightness } from '../charsets'
import {
  calculateCharacterAnimation,
  applyPulseToCharacterBrightness,
  createStaticContext,
  hasGrayscaleCache,
} from './animationHelpers'

// Claude风格字符集 - 强调区块字符
const CLAUDE_CHARSET = ' .:░▒▓█#@'

export class ClaudeEngine implements IRenderEngine {
  readonly name: StyleType = 'claude'

  /**
   * 渲染图片为 Claude 风格艺术
   * Wave 8.3: 支持灰度预计算缓存优化
   */
  render(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    canvas: HTMLCanvasElement,
    context?: AnimationContext
  ): RenderResult {
    // 使用提供的动画上下文，或创建静态上下文
    const animContext: RenderContextExt = context ?? createStaticContext()

    const imageData = this.getImageData(source)
    const { width: imgWidth, height: imgHeight } = imageData

    const { fontSize, charSpacing, contrast, brightness, invert, claudeDensity } = settings

    // Claude风格使用更紧凑的间距
    const adjustedSpacing = charSpacing * (1 - claudeDensity * 0.3)
    const charWidth = fontSize * 0.6 * adjustedSpacing
    const charHeight = fontSize

    const cols = Math.max(1, Math.floor(imgWidth / charWidth))
    const rows = Math.max(1, Math.floor(imgHeight / charHeight))

    canvas.width = cols * charWidth
    canvas.height = rows * charHeight

    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = settings.backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    ctx.font = `${fontSize}px ${settings.renderFont}`
    ctx.textBaseline = 'top'

    // Claude默认使用绿色
    const effectiveColorMode = settings.colorMode === 'grayscale' ? 'custom' : settings.colorMode
    const effectiveCustomColor = settings.colorMode === 'grayscale' ? '#00ff99' : settings.customColor

    const sampleWidth = imgWidth / cols
    const sampleHeight = imgHeight / rows
    const skipProbability = 1 - claudeDensity

    // 检查动画或鼠标交互是否启用
    const animationEnabled = settings.ditherAnimationEnabled || settings.dither2AnimationEnabled
    const mouseInteractionEnabled = settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled
    const needsAnimation = animationEnabled || mouseInteractionEnabled

    // Wave 8.3: 检查是否有灰度缓存
    const useCache = hasGrayscaleCache(animContext)
    const grayscaleCache = animContext.grayscaleCache

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // 密度检查
        if (Math.random() < skipProbability) continue

        // Wave 8.3: 智能采样
        const { brightness: rawBrightness, color } = useCache && grayscaleCache
          ? samplePixelFast(grayscaleCache, col * sampleWidth, row * sampleHeight, sampleWidth, sampleHeight)
          : samplePixel(imageData, col * sampleWidth, row * sampleHeight, sampleWidth, sampleHeight)

        let finalBrightness = useCache
          ? rawBrightness
          : applyContrastAndBrightness(rawBrightness, contrast, brightness * 255)

        if (invert) finalBrightness = invertBrightness(finalBrightness)

        // 计算字符基础位置
        const baseX = col * charWidth
        const baseY = row * charHeight

        // 应用动画或鼠标交互效果
        let drawX = baseX
        let drawY = baseY

        if (needsAnimation) {
          const animResult = calculateCharacterAnimation(
            baseX, baseY, animContext, settings, charWidth, charHeight
          )

          drawX += animResult.offsetX
          drawY += animResult.offsetY

          // 应用脉动效果到亮度（仅动画）
          if (animationEnabled) {
            finalBrightness = applyPulseToCharacterBrightness(finalBrightness, animResult.pulseFactor)
          }
        }

        const char = getCharByBrightness(finalBrightness, CLAUDE_CHARSET)
        const finalColor = applyColorMode(color, effectiveColorMode, effectiveCustomColor)
        ctx.fillStyle = this.applyOpacity(finalColor, settings.opacity)
        ctx.fillText(char, drawX, drawY)
      }
    }

    return { canvas, width: canvas.width, height: canvas.height, charWidth, charHeight }
  }

  getOutputSize(imageWidth: number, imageHeight: number, settings: FullRenderSettings): { width: number; height: number } {
    const { fontSize, charSpacing, claudeDensity } = settings
    const adjustedSpacing = charSpacing * (1 - claudeDensity * 0.3)
    const charWidth = fontSize * 0.6 * adjustedSpacing
    const charHeight = fontSize
    return { width: Math.floor(imageWidth / charWidth) * charWidth, height: Math.floor(imageHeight / charHeight) * charHeight }
  }

  private getImageData(source: HTMLImageElement | HTMLCanvasElement | ImageData): ImageData {
    if (source instanceof ImageData) return source
    const canvas = document.createElement('canvas')
    canvas.width = source.width
    canvas.height = source.height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)
    return ctx.getImageData(0, 0, canvas.width, canvas.height)
  }

  private applyOpacity(color: RGB, opacity: number): string {
    return opacity < 1 ? `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})` : `rgb(${color.r}, ${color.g}, ${color.b})`
  }
}

export const claudeEngine = new ClaudeEngine()